import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Delete } from '@yme/assets/icons';
import { ICON_ONLY, SIZE_36, TEXT_VARIANT } from '@yme/atoms/button';
import { Image } from '@yme/atoms/image';
import { ImageErrorPlaceholder } from '@yme/atoms/image-error-placeholder';
import { useProductProperties } from '@yme/atoms/product-properties';
import { ProductType } from '@yme/common/constants';
import { setDeletingProduct } from '@yme/common/redux/cart/actions';
import { getIsCartConvertedToOrder } from '@yme/common/redux/cart/selectors';
import { changeItemQuantity } from '@yme/common/redux/cart/services';
import { Link } from '@yme/components/Link';
import { getIsInAuthor, getPDPLinks } from '@yme/selectors/common';
import { ABB_PRODUCT_NAME_LOCALIZED_KEY } from '@yme/selectors/constants';
import { useTranslation } from '@yme/services/i18n';
import { colorAccentError800Base, colorCoreBlack300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel3, RegularTextLevel5 } from '@yme/tokens/typography';
import { getAttributeByName, getItemImage, getImageSrc } from '@yme/utils/common';
import { AspectRatio, ImageSuffix } from '@yme/utils/constants';
import { formatPrice } from '@yme/utils/formatPrice';
import { getPdpLinkByCategory, getProductURL } from '@yme/utils/product';
import { DeleteButton, ImageWrapper, OrderItemInfo, OrderItemName, OrderItemStyled, ProductNameStyled, } from './styled';
export function PopupOrderItem({ item: { product: { variants, slug, categories }, quantity, }, isRightToLeft, rootLanguage, productName, productType, setIsLoading, handleRemoveProduct, }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const labels = {
        controlMethod: t('productCard_controlMethod_label'),
        startingMethod: t('productCard_startingMethodAndTilt_label'),
        shaftLength: t('productCard_shaftLength_label'),
        rotation: t('productCard_rotation_label'),
        colourName: t('productCard_colorSelector_label'),
        size: t('product_size_label'),
    };
    const { attributes, images, sku, prices } = variants[0];
    const { ProductProperties } = useProductProperties(attributes, labels);
    const isCartConvertedToOrder = useSelector(getIsCartConvertedToOrder);
    const pdpLinks = useSelector(getPDPLinks);
    const isInAuthor = useSelector(getIsInAuthor);
    const itemImage = getItemImage(images);
    const itemPathname = getPdpLinkByCategory(pdpLinks, categories);
    const itemUrl = getProductURL({
        path: itemPathname,
        isInAuthor,
        slug,
    });
    const currentProductName = productType === ProductType.UNIT
        ? getAttributeByName(attributes, ABB_PRODUCT_NAME_LOCALIZED_KEY)
        : productName;
    const [price] = prices;
    const hasDiscount = Boolean(price?.discount);
    const handleDeleteItem = useCallback(() => {
        setIsLoading(true);
        dispatch(setDeletingProduct({
            sku,
            productName,
        }));
        dispatch(changeItemQuantity(sku, 0))
            .then(() => {
            handleRemoveProduct();
        })
            .finally(() => setIsLoading(false));
    }, [dispatch, handleRemoveProduct, productName, setIsLoading, sku]);
    return (React.createElement(OrderItemStyled, null,
        React.createElement(ImageWrapper, { isRightToLeft: isRightToLeft }, itemImage ? (React.createElement(Link, { to: {
                pathname: itemUrl,
                hash: sku,
            } },
            React.createElement(Image, { src: getImageSrc(itemImage.url, ImageSuffix.Mobile), alt: itemImage.label, loading: "lazy", isFullHeight: false }))) : (React.createElement(ImageErrorPlaceholder, { aspectRatio: AspectRatio.Square }))),
        React.createElement(FlexWrapper, { isFullWidth: true, gap: "16px" },
            React.createElement(OrderItemInfo, { isRightToLeft: isRightToLeft },
                React.createElement(RegularTextLevel5, { color: colorCoreBlack300 }, sku),
                React.createElement(OrderItemName, { weight: 500 },
                    React.createElement("span", null, isRightToLeft ? ` x${quantity}` : `${quantity}x `),
                    "\u00A0",
                    React.createElement(Link, { to: {
                            pathname: itemUrl,
                            hash: sku,
                        } },
                        React.createElement(ProductNameStyled, null, currentProductName))),
                React.createElement(ProductProperties, null)),
            React.createElement(FlexWrapper, { flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end" },
                price && (React.createElement(FlexWrapper, { flexDirection: "column", alignItems: "flex-end" },
                    React.createElement(RegularTextLevel3, { weight: 500 }, formatPrice({
                        locale: rootLanguage,
                        currencyName: price.currencyCode,
                        price: (price.discount?.discountedPrice ?? price.amount) * quantity,
                    })),
                    hasDiscount && (React.createElement(RegularTextLevel5, { color: colorAccentError800Base, isLineThrough: true }, formatPrice({
                        locale: rootLanguage,
                        currencyName: price.currencyCode,
                        price: price.amount * quantity,
                    }))))),
                React.createElement(DeleteButton, { isRightToLeft: isRightToLeft, buttonVariant: TEXT_VARIANT, hasIconButton: ICON_ONLY, size: SIZE_36, iconComponent: Delete, iconRotate: isRightToLeft ? 180 : 0, onClick: handleDeleteItem, disabled: isCartConvertedToOrder })))));
}
